import Button from '@cobliteam/ui/components/Button';
import Dropdown from '@cobliteam/ui/components/Dropdown';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import InputTime from '../../Form/InputTime';

const spaceRegex = /\s+/g;
const isHourInputComplete = (hourStr: string) => hourStr.replace(spaceRegex, '').length === 5;
const isHourInputEmpty = (hourStr: string) => hourStr.replace(spaceRegex, '').length === 0;

interface SelectTimeServiceWindowProps {
  size?: 'sm' | 'md';
  selectedHours?: [string, string];
  onChange: (hours?: [string, string]) => void;
}

const SelectTimeServiceWindow = ({ size = 'md', selectedHours, onChange }: SelectTimeServiceWindowProps) => {
  const selectedStartHour = selectedHours?.[0];
  const selectedEndHour = selectedHours?.[1];
  const [forceClosed, setForceClosed] = useState(false);
  const [startHour, setStartHour] = useState(selectedStartHour || undefined);
  const [endHour, setEndHour] = useState(selectedEndHour || undefined);
  const isStartHourComplete = isHourInputComplete(startHour || '');
  const isEndHourComplete = isHourInputComplete(endHour || '');
  const areAllInputsComplete = isStartHourComplete && isEndHourComplete;

  const areInputsValid = (startHourStr: string, endHourStr: string) => {
    const areAllInputsEmpty = isHourInputEmpty(startHourStr) && isHourInputEmpty(endHourStr);
    if (areAllInputsEmpty) return true;
    return areAllInputsComplete;
  };

  const areAllInputsValid = areInputsValid(startHour || '', endHour || '');
  const hasChanged = (startHour || '') !== (selectedStartHour || '') && endHour !== (selectedEndHour || '');
  const enableApplyButton = hasChanged && areAllInputsValid;

  const selectionCrossesDay = areAllInputsComplete && Number.parseInt(startHour!.replace(':', ''), 10) > Number.parseInt(endHour!.replace(':', ''), 10);

  const startHourInputRef = useRef<HTMLInputElement | null>(null);
  const endHourInputRef = useRef<HTMLInputElement | null>(null);

  const onClick = useCallback(() => {
    setStartHour(selectedStartHour || undefined);
    setEndHour(selectedEndHour || undefined);
  }, [selectedStartHour, selectedEndHour]);

  const handleClear = useCallback(() => {
    setStartHour(undefined);
    setEndHour(undefined);

    // This is needed to manually clean the input fields
    // @ts-ignore
    startHourInputRef.current.value = '';
    // @ts-ignore
    endHourInputRef.current.value = '';
  }, []);

  const handleClose = useCallback(() => {
    setForceClosed(true);
  }, []);

  const handleApply = useCallback(() => {
    onChange([startHour || '', endHour || '']);
    handleClose();
  }, [startHour, endHour, handleClose, onChange]);

  useEffect(() => {
    if (forceClosed) setForceClosed(false);
  }, [forceClosed]);

  const handleStartInputchange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setStartHour(value);
  };

  const handleEndInputchange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setEndHour(value);
  };

  return (
    <Dropdown
      onOpenChange={onClick}
      forceClose={forceClosed}
      overlay={
        <div className='flex w-96 flex-col gap-2.5 rounded-md border border-gray-light bg-white py-2.5'>
          <Dropdown.Header className='px-5'>
            <Button variant='ghost' disabled={startHour === undefined && endHour === undefined} onClick={handleClear}>
              Limpar seleção
            </Button>
          </Dropdown.Header>

          <div className='mt-2.5 flex max-h-64 flex-col'>
            <div className='mx-5 flex items-center'>
              <Typography className='w-fit' variant='caption-default'>
                Mostrar eventos de:&ensp;
              </Typography>
              <span>
                <InputTime
                  placeholder='09:00'
                  className='w-20'
                  autoFocus
                  onChange={handleStartInputchange}
                  value={startHour || ''}
                  defaultValue={selectedStartHour || ''}
                  error={startHour !== undefined && !isStartHourComplete}
                  inputRef={startHourInputRef}
                />
              </span>

              <Typography>&ensp;até&ensp;</Typography>
              <span>
                <InputTime
                  placeholder='18:00'
                  className='w-24'
                  onChange={handleEndInputchange}
                  value={endHour || ''}
                  defaultValue={selectedEndHour || ''}
                  error={endHour !== undefined && !isEndHourComplete}
                  inputRef={endHourInputRef}
                />
                <span>{selectionCrossesDay && <Typography className='text-gray'>(+1)</Typography>}</span>
              </span>
            </div>
            {selectionCrossesDay && (
              <div className='mx-5 mt-3 w-72 rounded bg-gray-lightest px-3 py-2'>
                <Typography variant='caption-default' className='text-gray-dark'>
                  Filtrando eventos com início em dias distintos.
                </Typography>
              </div>
            )}
          </div>
          <Dropdown.Footer>
            <Dropdown.Actions className='px-5'>
              <Button variant='ghost' full size='sm' onClick={handleClose}>
                Cancelar
              </Button>
              <Button size='sm' full onClick={handleApply} disabled={!enableApplyButton}>
                Aplicar
              </Button>
            </Dropdown.Actions>
          </Dropdown.Footer>
        </div>
      }
    >
      <Button variant='ghost' size={size} startIcon={<Icon name='clock' />}>
        Horário {selectedStartHour !== undefined && selectedStartHour !== '' ? '(1)' : ''}
      </Button>
    </Dropdown>
  );
};

SelectTimeServiceWindow.displayName = 'SelectTimeServiceWindow';

export default SelectTimeServiceWindow;
